/**
 * StepRail — the execution timeline (spec §4.7): a vertical rail with one
 * circle per step beside the existing step cards.
 *
 * `displayStatusOf` MUST match `StepProgressRow`'s own promotion of an
 * in-flight analysis step to `running` (`WorkstreamPage.tsx`) — that page
 * calls this same function rather than recomputing the rule, so the rail,
 * the step badge, the "Currently on step N" header line, and the card's two
 * Analyze/Write stage rows can never disagree on the same row. EXTEND this
 * function; never fork it.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { StepSummary } from '../../types/workstream';
import { TERMINAL_WORKSTREAM_STATUSES } from '../../types/workstream';

/**
 * The step's DISPLAYED status: raw `step.status`, except that an in-flight
 * analysis step is promoted to `running`.
 *
 * `workstreamStatus` is the workstream's live status and is what stops a
 * TERMINAL workstream from showing an active analysis/report spinner off a
 * STALE `analysis_phase` (spec §15): a cancelled or failed run leaves the
 * acquisition row at `flagged` with `analysis_phase` still `analysis`/`report`,
 * which is precisely the shape this promotion fires on. It is optional only so
 * the promotion rule keeps working for callers that legitimately have no
 * workstream in hand; every call site inside the page passes it.
 */
export function displayStatusOf(step: StepSummary, workstreamStatus?: string | null): string {
  const terminal = !!workstreamStatus && TERMINAL_WORKSTREAM_STATUSES.has(workstreamStatus);
  const active = !terminal
    && step.kind === 'analysis'
    && step.analysis_phase
    && step.analysis_phase !== 'complete'
    && (step.status === 'done' || step.status === 'flagged');
  return active ? 'running' : String(step.status);
}

// Paler palette (product spec): finished/failed markers are softened so a
// green "done" doesn't read as a hard "verified 100%" claim. Done stays a
// MUTED green with the amber flag preserved for flagged steps (the chosen
// mitigation for the green-confidence concern); flip `done` to amber here to
// switch to the all-yellow model. The running marker stays a touch stronger
// than the rest because its pulse + ring is the "you are here" cue.
const CIRCLE: Record<string, string> = {
  done: 'bg-emerald-300 border-emerald-300',
  flagged: 'bg-amber-300 border-amber-300',
  running: 'bg-blue-400 border-blue-400',
  failed: 'bg-red-300 border-red-300',
  pending: 'bg-white border-slate-300',
  skipped: 'bg-white border-slate-200',
  cancelled: 'bg-white border-slate-200',
};

export function StepRail({
  steps, renderStep, workstreamStatus,
}: {
  steps: StepSummary[];
  renderStep: (step: StepSummary) => ReactNode;
  /** Live workstream status — forwarded to `displayStatusOf` so the rail
   *  inherits the terminal-workstream guard instead of restating it. */
  workstreamStatus?: string | null;
}) {
  // Staged reveal (spec §4.8) via a CSS TRANSITION, not an animation. Two
  // reasons: it needs no Tailwind keyframe config, and `transitionDelay`
  // cannot clash with the running circle's `animate-pulse` the way
  // `animationDelay` on the same element would — that would delay the pulse
  // itself instead of delaying the circle's reveal.
  //
  // `motion-safe:` gates every ANIMATION utility here (transition,
  // translate, and the from-opacity-0 fade-in start). This codebase has no
  // global `prefers-reduced-motion` CSS (checked: no such media query
  // anywhere under frontend/src), so the per-utility Tailwind variant is the
  // only mechanism available — without it, a reduced-motion user still gets
  // the full translate/opacity transition on the rail line and every row.
  // The inline `transitionDelay` stays unconditional: with
  // `motion-safe:transition-all` absent under reduced motion there is no
  // transition for the delay to apply to, so it is inert rather than
  // harmful.
  //
  // The one exception is the PENDING-DULLED opacity target (60%, below) —
  // deliberately NOT motion-safe-gated. Do not "fix" that back. 60% opacity
  // is a SEMANTIC state indicator ("this step has not started"), not an
  // animation; motion-safe exists to gate animation, never a permanent state
  // signal. Gating it made reduced-motion users lose the pending/active
  // distinction entirely (no opacity utility ever applied for them), which
  // is an accessibility regression strictly worse than the cascade bug it
  // was meant to fix. `opacity-60` (bare) is therefore always active once
  // revealed, in every motion setting; only the ANIMATED path to get there
  // (the transition, and the from-value of opacity-0) is motion-gated.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => { setRevealed(true); }, []);
  // `revealedOpacity` is the REVEALED-state target, not a second competing
  // utility: a pending card's dulled 60% opacity used to be laid on top of
  // this helper's unconditional `motion-safe:opacity-100` as a bare
  // `opacity-60` class on the same element. Both utilities land on the same
  // element; Tailwind emits `motion-safe:` variants AFTER base utilities, so
  // under normal motion settings `motion-safe:opacity-100` always won the
  // cascade and pending cards silently rendered at full opacity (spec §4.7
  // P2-1). Folding the dulled target INTO the revealed state — one opacity
  // utility per element per state — removes the conflict outright. The
  // dulled branch (`opacity-60`) is intentionally bare/ungated (see comment
  // above `revealed`'s declaration) — the non-dulled branch
  // (`motion-safe:opacity-100`) stays gated, so the two branches remain
  // mutually exclusive on any one element regardless of motion setting.
  const reveal = (delayMs: number, revealedOpacity: 100 | 60 = 100) => ({
    className: [
      'motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out',
      revealed
        ? `${revealedOpacity === 60 ? 'opacity-60' : 'motion-safe:opacity-100'} motion-safe:translate-y-0`
        : 'motion-safe:opacity-0 motion-safe:translate-y-1',
    ].join(' '),
    style: { transitionDelay: `${delayMs}ms` },
  });

  return (
    // The rail line is a sibling of the <ol>, never a child: a <span> is not
    // valid content for a list element.
    <div className="relative" data-testid="step-rail">
      <span
        aria-hidden="true"
        className={`absolute left-[11px] top-2 bottom-2 w-px bg-slate-200 ${reveal(0).className}`}
        style={reveal(0).style}
      />
      <ol className="relative space-y-2">
        {steps.map((step, i) => {
          const status = displayStatusOf(step, workstreamStatus);
          const running = status === 'running';
          const dulled = status === 'pending';
          const circle = reveal(80 + i * 80);                          // circles first…
          const card = reveal(160 + i * 80, dulled ? 60 : 100);        // …then the step text
          return (
            <li key={step.ordinal} className="relative flex gap-3">
              <span
                data-testid={`rail-circle-${step.ordinal}`}
                className={`relative z-10 mt-4 h-[22px] w-[22px] shrink-0 rounded-full border-2 ${CIRCLE[status] ?? CIRCLE['pending']} ${running ? 'motion-safe:animate-pulse' : ''} ${circle.className}`}
                style={circle.style}
              />
              <div
                className={`min-w-0 flex-1 rounded-lg ${
                  running ? 'ring-2 ring-blue-400/60 shadow-[0_0_18px_rgba(59,130,246,0.35)]' : ''
                } ${card.className}`}
                style={card.style}
                data-testid={`rail-card-${step.ordinal}`}
              >
                {renderStep(step)}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
